import { useState, type SyntheticEvent } from 'react'
import { Button } from '@/components/ui/button'
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'

type NamedItem = { id: string; name: string; active: boolean }

/** Side panel to add or edit something that has only a name and an active state. */
export function NamePanel({
  open,
  item,
  noun,
  placeholder,
  inactiveHint,
  pending,
  error,
  onOpenChange,
  onSubmit
}: {
  open: boolean
  item: NamedItem | null
  noun: string
  placeholder: string
  inactiveHint: string
  pending: boolean
  error: { message: string } | null
  onOpenChange(open: boolean): void
  onSubmit(values: { name: string; active: boolean }): void
}): React.JSX.Element {
  // Keep showing the last item while the panel slides closed, instead of the empty form.
  const [shown, setShown] = useState(item)
  if (open && item !== shown) setShown(item)
  const displayed = open ? item : shown

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{displayed ? displayed.name : `New ${noun}`}</SheetTitle>
          <SheetDescription>{inactiveHint}</SheetDescription>
        </SheetHeader>
        <NameForm
          key={displayed?.id ?? 'new'}
          item={displayed}
          placeholder={placeholder}
          pending={pending}
          error={error}
          submitLabel={displayed ? `Save ${noun}` : `Add ${noun}`}
          onCancel={() => onOpenChange(false)}
          onSubmit={onSubmit}
        />
      </SheetContent>
    </Sheet>
  )
}

function NameForm({
  item,
  placeholder,
  pending,
  error,
  submitLabel,
  onCancel,
  onSubmit
}: {
  item: NamedItem | null
  placeholder: string
  pending: boolean
  error: { message: string } | null
  submitLabel: string
  onCancel(): void
  onSubmit(values: { name: string; active: boolean }): void
}): React.JSX.Element {
  const [name, setName] = useState(item?.name ?? '')
  const [active, setActive] = useState(item?.active ?? true)

  function submit(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): void {
    event.preventDefault()
    if (name.trim()) onSubmit({ name: name.trim(), active })
  }

  return (
    <form className="flex flex-col gap-4 px-4 pb-6" onSubmit={submit}>
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="item-name">Name</FieldLabel>
          <Input
            id="item-name"
            autoFocus
            value={name}
            placeholder={placeholder}
            onChange={(event) => setName(event.target.value)}
            disabled={pending}
          />
        </Field>
        {item && (
          <Field orientation="horizontal">
            <Switch
              id="item-active"
              nativeButton
              render={<button type="button" />}
              checked={active}
              onCheckedChange={setActive}
              disabled={pending}
            />
            <FieldLabel htmlFor="item-active">Active</FieldLabel>
          </Field>
        )}
      </FieldGroup>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error.message}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={pending || !name.trim()}>
          {pending ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </form>
  )
}
